import { PresetGrid } from "./PresetGrid";
import { ANIMATIONS } from "@/lib/presets";
import { DEVANAGARI_FONTS, LATIN_FONTS } from "@/lib/fonts";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-[var(--cc-text-2)] block mb-1">{label}</span>
    {children}
  </label>
);
const sel = "w-full h-10 rounded-md bg-[var(--cc-elev)] border border-[var(--cc-border-strong)] px-2 text-sm";

const Color = ({ label, k, style, set }) => (
  <label className="flex items-center gap-2 text-xs font-semibold text-[var(--cc-text-2)]">
    <input type="color" value={style[k]} onChange={(e) => set({ [k]: e.target.value })} data-testid={`style-${k}`} className="h-9 w-10 rounded bg-transparent border border-[var(--cc-border-strong)]" />
    {label}
  </label>
);

const Range = ({ label, k, min, max, step = 1, style, set }) => (
  <Field label={`${label}: ${style[k]}`}>
    <input type="range" min={min} max={max} step={step} value={style[k]} onChange={(e) => set({ [k]: +e.target.value })} data-testid={`style-${k}`} />
  </Field>
);

export const StylePanel = ({ style, setStyle }) => {
  const set = (patch) => setStyle((s) => ({ ...s, ...patch }));
  const p = { style, set };
  return (
    <div className="space-y-6">
      <PresetGrid style={style} setStyle={setStyle} />
      <div className="space-y-4" data-testid="style-controls">
        <h2 className="text-xs font-semibold tracking-wider uppercase text-[var(--cc-text-2)]">Customise</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Font (${DEVANAGARI_FONTS.length + LATIN_FONTS.length})`}>
            <select value={style.font} onChange={(e) => set({ font: e.target.value })} className={sel} data-testid="style-font">
              <optgroup label="Hindi + English (Devanagari)">{DEVANAGARI_FONTS.map((f) => <option key={f}>{f}</option>)}</optgroup>
              <optgroup label="English / Latin (Hindi uses fallback)">{LATIN_FONTS.map((f) => <option key={f}>{f}</option>)}</optgroup>
            </select>
          </Field>
          <Field label={`Animation (${ANIMATIONS.length})`}>
            <select value={style.animation} onChange={(e) => set({ animation: e.target.value })} className={sel} data-testid="style-animation">
              {ANIMATIONS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
            </select>
          </Field>
          <Field label="Weight">
            <select value={style.weight} onChange={(e) => set({ weight: +e.target.value })} className={sel} data-testid="style-weight">
              {[400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </Field>
          <Field label="Position">
            <select value={style.position} onChange={(e) => set({ position: e.target.value })} className={sel} data-testid="style-position">
              <option value="top">Top</option><option value="middle">Middle</option><option value="bottom">Bottom</option>
            </select>
          </Field>
          <Field label="Background">
            <select value={style.bgMode} onChange={(e) => set({ bgMode: e.target.value })} className={sel} data-testid="style-bgMode">
              <option value="none">None</option><option value="box">Box</option><option value="line">Per line</option>
            </select>
          </Field>
          <Field label="Words on screen">
            <select value={style.chunk} onChange={(e) => set({ chunk: +e.target.value })} className={sel} data-testid="style-chunk">
              <option value={0}>Whole caption</option>{[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} at a time</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Color label="Text" k="color" {...p} />
          <Color label="Highlight" k="highlightColor" {...p} />
          <Color label="Outline" k="outlineColor" {...p} />
          <Color label="Shadow" k="shadowColor" {...p} />
          <Color label="Background" k="bgColor" {...p} />
          <label className="flex items-center gap-2 text-xs font-semibold text-[var(--cc-text-2)] min-h-[36px]">
            <input type="checkbox" checked={style.uppercase} onChange={(e) => set({ uppercase: e.target.checked })} data-testid="style-uppercase" className="h-4 w-4" /> UPPERCASE
          </label>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <Range label="Size" k="size" min={3} max={14} step={0.5} {...p} />
          <Range label="Outline" k="outlineWidth" min={0} max={16} {...p} />
          <Range label="Shadow blur" k="shadowBlur" min={0} max={40} {...p} />
          <Range label="Shadow offset" k="shadowY" min={0} max={12} {...p} />
          <Range label="Background opacity" k="bgOpacity" min={0} max={1} step={0.05} {...p} />
        </div>
        <p className="text-xs text-[var(--cc-muted)]">Word highlighting uses Whisper's real word timestamps when 'Exact word timing' was on (green 'word timing' badge in the editor). If you change the number of words in a caption, its timing falls back to an even estimate.</p>
      </div>
    </div>
  );
};
